import { Checkbox } from '@xing-com/checkbox';
import type {
  CountOptions,
  FilterName,
  Option,
} from '@xing-com/crate-jobs-domain-search-types';
import type { RemoteOptionAggregationFragment } from '@xing-com/crate-jobs-graphql';
import { Tag } from '@xing-com/tag';
import { BodyCopy } from '@xing-com/typography';

import { LimitedJobCount } from '../../shared';

import * as S from './filter-item.styles';

type Props = {
  name: FilterName;
  option: NonNullable<Option>;
  active: boolean;
  onClick: (id: string) => void;
  maxWidth?: string;
};

const isRemoteOption = (
  option: Exclude<Option, undefined | null>
): option is RemoteOptionAggregationFragment =>
  option.__typename === 'JobAggregationRemoteOption';

const FilterCheckbox: React.FC<Props> = ({ name, option, active, onClick }) => {
  const handleOnClick = (): void => {
    onClick(option.id);
  };

  // oxlint-disable-next-line typescript/consistent-type-assertions
  const count = (option as CountOptions).count;

  return (
    <S.CheckboxItemContainer>
      <Checkbox on={active} onChange={handleOnClick} />
      <BodyCopy size="small" noMargin>
        {/* @ts-expect-error The name is hardcoded to the option type, meaning we know the index is correct */}
        {option[name]?.localizationValue}
        {count >= 0 ? (
          <>
            {' '}
            (<LimitedJobCount count={count} />)
          </>
        ) : null}
      </BodyCopy>
    </S.CheckboxItemContainer>
  );
};

const FilterTag: React.FC<Props> = ({
  name,
  option,
  active,
  onClick,
  maxWidth,
}) => {
  const isRemoteTag = isRemoteOption(option);

  const handleOnClick = (): void => {
    onClick(option.id);
  };

  return (
    <Tag selected={active} onClick={handleOnClick} maxWidth={maxWidth}>
      {/* @ts-expect-error Providing complete type-safe access is really complex for this case*/}
      {option[name]?.localizationValue}
      {isRemoteTag || option.count < 0 ? null : (
        <>
          {' '}
          (<LimitedJobCount count={option.count} />)
        </>
      )}
    </Tag>
  );
};

type FilterItemProps = {
  asCheckbox: boolean;
} & Props;

export const FilterItem: React.FC<FilterItemProps> = ({
  asCheckbox,
  ...props
}) => (asCheckbox ? <FilterCheckbox {...props} /> : <FilterTag {...props} />);
